import { branchRoutePath } from "./oauth/resource";
import { MAX_SLUG_LENGTH } from "./slug";
import { splitHost } from "./spaceDomain";

export type TenantEdgeUrlOptions = {
  edgeUrl: string;
  edgeWildcardDomain: string | undefined;
  combinedSpaceTenantLabel?: string;
  customDomain?: string;
  legacyCustomDomain?: string;
};

export type TenantEdgeUrlConfig = {
  combinedSpaceTenantHosts: boolean;
  stackEdgeBaseDomain?: string;
};

export function maxSpaceSlugLength({
  combinedSpaceTenantLabel,
}: TenantEdgeUrlOptions): number {
  return combinedSpaceTenantLabel
    ? MAX_SLUG_LENGTH - 2 - combinedSpaceTenantLabel.length
    : MAX_SLUG_LENGTH;
}

export function tenantEdgeUrlOptionsFromTenant(
  tenant: {
    edgeOrigin: string;
    edgeWildcardDomain: string | null | undefined;
  },
  config: TenantEdgeUrlConfig
): TenantEdgeUrlOptions {
  const withoutWildcard = {
    edgeUrl: tenant.edgeOrigin,
    edgeWildcardDomain: undefined,
  };
  const edgeWildcardDomain = tenant.edgeWildcardDomain ?? undefined;
  if (!edgeWildcardDomain) return withoutWildcard;
  const edgeHostname = new URL(tenant.edgeOrigin).hostname;
  const split = splitHost(edgeHostname);
  if (
    !split ||
    (split.parent !== edgeWildcardDomain.toLowerCase() &&
      split.parent !== config.stackEdgeBaseDomain?.toLowerCase())
  ) {
    return {
      ...withoutWildcard,
      edgeWildcardDomain,
      customDomain: edgeWildcardDomain,
    };
  }
  return {
    edgeUrl: tenant.edgeOrigin,
    edgeWildcardDomain: split.parent,
    combinedSpaceTenantLabel: split.label,
    legacyCustomDomain: edgeWildcardDomain,
  };
}

// Routes may repeat across spaces only when each space has its own edge host.
export function edgeRouteScope(
  options: TenantEdgeUrlOptions
): "space" | "tenant" {
  return options.edgeWildcardDomain ? "space" : "tenant";
}

export function spaceEdgeHostnameSuffix(
  opts: {
    edgeWildcardDomain?: string;
    combinedSpaceTenantLabel?: string;
    customDomain?: string;
    legacyCustomDomain?: string;
  },
  customDomain?: string | null,
  spaceSlug?: string
): string | null {
  customDomain ||= opts.customDomain;
  if (spaceSlug?.length === 2) customDomain ||= opts.legacyCustomDomain;
  if (customDomain) {
    return `.${customDomain}`;
  }
  if (opts.edgeWildcardDomain && opts.combinedSpaceTenantLabel) {
    return `--${opts.combinedSpaceTenantLabel}.${opts.edgeWildcardDomain}`;
  }
  return null;
}

function spaceEdgeHostname(
  spaceSlug: string,
  opts: TenantEdgeUrlOptions,
  customDomain?: string | null
): string | null {
  const suffix = spaceEdgeHostnameSuffix(opts, customDomain, spaceSlug);
  return suffix ? `${spaceSlug}${suffix}` : null;
}

export function spaceEdgeOrigin(
  spaceSlug: string,
  opts: TenantEdgeUrlOptions,
  customDomain?: string | null,
  customHostname?: string | null
): string {
  if (customHostname) return `https://${customHostname}`;
  const hostname = spaceEdgeHostname(spaceSlug, opts, customDomain);
  if (!hostname) return opts.edgeUrl;
  if (customDomain) return `https://${hostname}`;
  const origin = new URL(opts.edgeUrl);
  origin.hostname = hostname;
  return origin.origin;
}

export function edgeRouteUrl(
  origin: string,
  route: string,
  params?: { branchId?: string | null; externalId?: string | null }
): string {
  const path = params?.branchId
    ? branchRoutePath(params.branchId, route)
    : route;
  const base = `${origin}${path}`;
  const search = new URLSearchParams();
  if (params?.externalId) search.set("external_id", params.externalId);
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}
